import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const STORAGE_KEY = "bicimone-theme";

function applyResolved(pref) {
  const mql = window.matchMedia("(prefers-color-scheme: dark)");
  const resolved = pref === "system" ? (mql.matches ? "dark" : "light") : pref;
  document.documentElement.classList.toggle("dark", resolved === "dark");
  return resolved;
}

export function ThemeProvider({ children }) {
  const { user, setUser } = useAuth() || {};
  const [theme, setThemeState] = useState(() => localStorage.getItem(STORAGE_KEY) || "system");
  const [resolved, setResolved] = useState("dark");
  const syncedFor = useRef(null);

  // Apply on mount and whenever preference changes; follow system if "system"
  useEffect(() => {
    setResolved(applyResolved(theme));
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => { if (theme === "system") setResolved(applyResolved("system")); };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [theme]);

  // When the logged-in user's saved theme loads, adopt it (once per user)
  useEffect(() => {
    if (user && user.theme && syncedFor.current !== user.user_id) {
      syncedFor.current = user.user_id;
      if (user.theme !== theme) {
        setThemeState(user.theme);
        localStorage.setItem(STORAGE_KEY, user.theme);
      }
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTheme = useCallback(async (pref) => {
    setThemeState(pref);
    localStorage.setItem(STORAGE_KEY, pref);
    setResolved(applyResolved(pref));
    if (user) {
      try {
        const res = await api.put("/auth/preferences", { theme: pref });
        if (setUser) setUser(res.data);
      } catch (e) { /* preference persists locally regardless */ }
    }
  }, [user, setUser]);

  const toggle = useCallback(() => setTheme(resolved === "dark" ? "light" : "dark"), [resolved, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
}
